const externalOrRelativeUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) return false;
  if (value.startsWith("/")) return true;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};

const linkValues = (value, path = []) => {
  if (Array.isArray(value))
    return value.flatMap((item, index) => linkValues(item, [...path, index]));
  if (!value || typeof value !== "object") return [];
  return Object.entries(value).flatMap(([key, child]) => {
    const nextPath = [...path, key];
    if (key === "url" && typeof child === "string")
      return [{ value: child, path: nextPath.join(".") }];
    return linkValues(child, nextPath);
  });
};

const missingImageDescriptions = (block) => {
  const props = block.props || {};
  const missing = [];
  if (
    props.image_url &&
    !props.image_decorative &&
    !(props.image_alt || props.alt)?.trim()
  )
    missing.push("section image");
  (props.members || []).forEach((member, index) => {
    if (
      member.image_url &&
      !member.image_decorative &&
      !member.image_alt?.trim()
    )
      missing.push(`team member ${index + 1}`);
  });
  (props.items || []).forEach((item, index) => {
    if (
      item.logo_url &&
      !item.logo_decorative &&
      !item.logo_alt?.trim()
    )
      missing.push(`logo ${index + 1}`);
  });
  return missing;
};

const finding = ({ id, severity, title, detail, page, block = null }) => ({
  id,
  severity,
  title,
  detail,
  pageId: page?.page_id || null,
  pageTitle: page?.title || "Website",
  blockId: block?.block_id || null,
});

export const buildWebsiteLaunchReport = (pages = []) => {
  const checks = [];
  const publishablePages = pages.filter((page) => page.status !== "hidden");
  if (!publishablePages.length)
    checks.push(
      finding({
        id: "no-publishable-pages",
        severity: "blocker",
        title: "Add a publishable page",
        detail: "At least one page must be visible before this site can launch.",
      }),
    );

  publishablePages.forEach((page) => {
    const visibleBlocks = (page.blocks || []).filter(
      (block) => block.visibility !== "hidden",
    );
    if (!visibleBlocks.length)
      checks.push(
        finding({
          id: `empty-${page.page_id}`,
          severity: "blocker",
          title: `${page.title} has no visible content`,
          detail: "Show or add at least one section before publishing this page.",
          page,
        }),
      );
    if (!page.seo?.title?.trim())
      checks.push(
        finding({
          id: `seo-title-${page.page_id}`,
          severity: "warning",
          title: `Add an SEO title to ${page.title}`,
          detail: "A concise title helps this page appear clearly in search and browser tabs.",
          page,
        }),
      );
    if (!page.seo?.description?.trim())
      checks.push(
        finding({
          id: `seo-description-${page.page_id}`,
          severity: "warning",
          title: `Add an SEO description to ${page.title}`,
          detail: "Describe this page in one useful sentence for search results and link previews.",
          page,
        }),
      );

    const title = page.seo?.title?.trim();
    const description = page.seo?.description?.trim();
    if (title && (title.length < 20 || title.length > 60))
      checks.push(finding({
        id: `seo-title-length-${page.page_id}`, severity: "warning",
        title: `Refine the SEO title for ${page.title}`,
        detail: "Use a distinct title between 20 and 60 characters so search and answer engines can identify this page clearly.", page,
      }));
    if (description && (description.length < 50 || description.length > 160))
      checks.push(finding({
        id: `seo-description-length-${page.page_id}`, severity: "warning",
        title: `Refine the SEO description for ${page.title}`,
        detail: "Use a factual description between 50 and 160 characters.", page,
      }));
    if (!visibleBlocks.some((block) => block.type === "hero" && block.props?.headline?.trim()))
      checks.push(finding({
        id: `heading-${page.page_id}`, severity: "warning",
        title: `Add a clear primary heading to ${page.title}`,
        detail: "A visible hero headline gives people, search engines, and assistive technology one clear page topic.", page,
      }));

    visibleBlocks.forEach((block) => {
      missingImageDescriptions(block).forEach((label, index) =>
        checks.push(
          finding({
            id: `image-alt-${block.block_id}-${index}`,
            severity: "blocker",
            title: `Describe the ${label}`,
            detail: "Add useful alternative text or mark the image as decorative.",
            page,
            block,
          }),
        ),
      );
      linkValues(block.props).forEach((link, index) => {
        if (externalOrRelativeUrl(link.value)) return;
        checks.push(
          finding({
            id: `link-${block.block_id}-${index}`,
            severity: "blocker",
            title: "Fix an unsafe or incomplete link",
            detail: `Use a site path or a full HTTPS address for ${link.path}.`,
            page,
            block,
          }),
        );
      });
    });
  });

  const normalizedTitles = new Map();
  publishablePages.forEach((page) => {
    const title = page.seo?.title?.trim().toLowerCase();
    if (!title) return;
    normalizedTitles.set(title, [...(normalizedTitles.get(title) || []), page]);
  });
  normalizedTitles.forEach((matchingPages) => {
    if (matchingPages.length < 2) return;
    matchingPages.forEach((page) => checks.push(finding({
      id: `duplicate-seo-title-${page.page_id}`, severity: "warning",
      title: `Give ${page.title} a unique SEO title`,
      detail: "Another visible page uses the same title, which makes page identity ambiguous.", page,
    })));
  });

  const hasConversionPath = publishablePages.some((page) =>
    (page.blocks || []).some(
      (block) =>
        block.visibility !== "hidden" &&
        ["cta", "registration_cta", "contact_form"].includes(block.type),
    ),
  );
  if (publishablePages.length && !hasConversionPath)
    checks.push(
      finding({
        id: "conversion-path",
        severity: "warning",
        title: "Add a clear next step",
        detail: "Consider a registration, contact, or call-to-action section so visitors know what to do next.",
        page: publishablePages[0],
      }),
    );

  const blockers = checks.filter((check) => check.severity === "blocker");
  const warnings = checks.filter((check) => check.severity === "warning");
  return { checks, blockers, warnings, ready: blockers.length === 0 };
};
